import { useState } from 'react';
import { message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { useOrganizationMutation } from '~/app/hooks/organization/useOrganizationMutation';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import type { FindOrganizationDto } from '@evimo/types/api/organization/find-organization/find-organization.dto';
import type { UpdateOrganizationRequestDto } from '@evimo/types/api/organization/update-organization/update-organization.request.dto';
import type { UpdateOrganizationResponseDto } from '@evimo/types/api/organization/update-organization/update-organization.response.dto';
import { useTriggerMetisOcppLogs } from './hooks/useTriggerMetisOcppLogs';
import { BackOfficePageView } from './BackOfficePageView';

export function BackOfficePageContainer() {
  const { isSuperAdmin, isTriggeringMetisLogs, onTriggerMetisLogs } =
    useTriggerMetisOcppLogs();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchValue, 300);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] =
    useState<FindOrganizationDto | null>(null);
  const { t } = useTranslation('organization');
  const {
    data: listResult,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organizations', page, pageSize, debouncedSearchValue],
    queryFn: () =>
      OrganizationService.listOrganizations({
        page,
        limit: pageSize,
        q: debouncedSearchValue || undefined,
      }),
  });

  const organizations = listResult?.data ?? [];
  const total = listResult?.meta.total ?? 0;

  const deleteOrganizationMutation = useOrganizationMutation<string, void>(
    (organizationId) =>
      OrganizationService.deleteOrganizationById(organizationId),
    {
      queryKey: ['organizations'],
      onSuccess: () => message.success(t('messages.deleteOrganizationSuccess')),
      onError: () => message.error(t('messages.deleteOrganizationFailed')),
    },
  );

  const toggleStatusMutation = useOrganizationMutation<
    UpdateOrganizationRequestDto & { orgId: string },
    UpdateOrganizationResponseDto
  >(({ orgId, ...dto }) => OrganizationService.updateOrganization(orgId, dto), {
    queryKey: ['organizations'],
    onSuccess: () => message.success(t('messages.updateOrganizationSuccess')),
    onError: () => message.error(t('messages.updateOrganizationFailed')),
  });

  const handleAddNew = (): void => {
    setEditingOrganization(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (organization: FindOrganizationDto): void => {
    setEditingOrganization(organization);
    setIsDialogOpen(true);
  };

  const handleDelete = (organizationId: string): void => {
    deleteOrganizationMutation.mutate(organizationId);
  };

  const handleStatusToggle = (organizationId: string): void => {
    const organization = organizations.find(
      (item) => item.id === organizationId,
    );

    if (!organization) {
      return;
    }

    toggleStatusMutation.mutate({
      orgId: organizationId,
      name: organization.name,
      organizationType: organization.organizationType,
      mainLanguage: organization.mainLanguage,
      active: !organization.active,
      parentId: organization.parentId,
    });
  };

  const handleSearchChange = (value: string): void => {
    setSearchValue(value);
    setPage(1);
  };

  const handlePageChange = (nextPage: number, nextPageSize: number): void => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBox
        title={t('page.errorLoadingOrganization')}
        description={error.message}
      />
    );
  }

  return (
    <BackOfficePageView
      editingOrganization={editingOrganization}
      isDialogOpen={isDialogOpen}
      isSuperAdmin={isSuperAdmin}
      isTriggeringMetisLogs={isTriggeringMetisLogs}
      onAddNew={handleAddNew}
      onDelete={handleDelete}
      onDialogOpenChange={setIsDialogOpen}
      onEdit={handleEdit}
      onStatusToggle={handleStatusToggle}
      onSearchChange={handleSearchChange}
      onPageChange={handlePageChange}
      onTriggerMetisLogs={onTriggerMetisLogs}
      organizations={organizations}
      searchValue={searchValue}
      total={total}
      page={page}
      pageSize={pageSize}
    />
  );
}
